import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const revealSelectors = [
  '.section_2 > h2',
  '.everything-section > h2',
  '.features-cta',
  '.print-section > h2',
  '.product-toolbar',
  '.product-category-heading',
  '.trusted > h3',
  '.ready-cta > h2',
  '.ready-cta > p',
  '.product-title',
  '.product-description',
  '.product-benefits',
  '.use-cases > h3',
  '.configuration-section .config-main > h2',
  '.why-choose-container > h2',
  '.why-choose-subtitle',
  '.related-products-container > h2',
  '.related-subtitle',
  '.profile-header',
  '.profile-quotes-heading',
  '.profile-empty',
];

const staggerGroups = [
  ['.sec2_cards_holder', '.sec2_card'],
  ['.features', '.feature-card'],
  ['.product-category-options', 'button'],
  ['.product-category-track', '.product-card-link'],
  ['.key-features', '.feature-item'],
  ['.use-cases-grid', '.use-case-item'],
  ['.why-choose-grid', '.why-choose-card'],
  ['.related-products-grid', '.related-product-card'],
  ['.profile-overview', '.profile-stat, .profile-logout'],
  ['.profile-quote-grid', '.profile-quote-card'],
  ['.footer-grid', '.footer-col'],
];

function directMatches(container, selector) {
  return Array.from(container.querySelectorAll(`:scope > ${selector}`));
}

function animateReveal(element, index = 0) {
  gsap.from(element, {
    autoAlpha: 0,
    y: 44,
    duration: 0.85,
    delay: Math.min(index * 0.015, 0.12),
    ease: 'power3.out',
    clearProps: 'transform,opacity,visibility',
    scrollTrigger: {
      trigger: element,
      start: 'top 88%',
      once: true,
    },
  });
}

function animateStaggerGroup(container, itemSelector) {
  const items = directMatches(container, itemSelector);
  if (!items.length) return;

  gsap.from(items, {
    autoAlpha: 0,
    y: 48,
    scale: 0.965,
    rotateX: 5,
    transformOrigin: '50% 100%',
    duration: 0.8,
    ease: 'power3.out',
    stagger: {
      each: 0.085,
      from: 'start',
    },
    clearProps: 'transform,opacity,visibility',
    scrollTrigger: {
      trigger: container,
      start: 'top 84%',
      once: true,
    },
  });
}

function animateProductIntro(isRtl) {
  const gallery = document.querySelector('.product-gallery');
  const info = document.querySelector('.product-info');
  if (!gallery || !info) return;

  const timeline = gsap.timeline({
    defaults: { duration: 0.95, ease: 'power3.out' },
    scrollTrigger: {
      trigger: '.product-details-section',
      start: 'top 78%',
      once: true,
    },
  });

  timeline
    .from(gallery, {
      autoAlpha: 0,
      x: isRtl ? 70 : -70,
      scale: 0.97,
      clearProps: 'transform,opacity,visibility',
    })
    .from(info, {
      autoAlpha: 0,
      x: isRtl ? -70 : 70,
      clearProps: 'transform,opacity,visibility',
    }, '-=0.72');
}

function animateConfiguration() {
  document.querySelectorAll('.config-group').forEach((group, index) => {
    gsap.from(group, {
      autoAlpha: 0,
      x: index % 2 === 0 ? -34 : 34,
      y: 18,
      duration: 0.72,
      ease: 'power3.out',
      clearProps: 'transform,opacity,visibility',
      scrollTrigger: {
        trigger: group,
        start: 'top 91%',
        once: true,
      },
    });
  });

  const summary = document.querySelector('.request-summary');
  if (summary) {
    gsap.from(summary, {
      autoAlpha: 0,
      y: 44,
      scale: 0.97,
      duration: 0.9,
      ease: 'power3.out',
      clearProps: 'transform,opacity,visibility',
      scrollTrigger: {
        trigger: summary,
        start: 'top 88%',
        once: true,
      },
    });
  }
}

function animateImageParallax() {
  document.querySelectorAll('.main-image img').forEach((image) => {
    gsap.fromTo(image, {
      yPercent: -2.5,
      scale: 1.045,
    }, {
      yPercent: 2.5,
      scale: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: image.closest('.product-gallery') || image,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1.1,
      },
    });
  });

  document.querySelectorAll('.sec2_card .card-image, .related-product-image img').forEach((image) => {
    gsap.from(image, {
      scale: 1.08,
      duration: 1.15,
      ease: 'power2.out',
      clearProps: 'transform',
      scrollTrigger: {
        trigger: image,
        start: 'top 90%',
        once: true,
      },
    });
  });
}

function animateHero() {
  const heroContent = document.querySelectorAll('.hero-slide.active .hero_box > *');
  if (!heroContent.length) return;

  gsap.from(heroContent, {
    autoAlpha: 0,
    y: 34,
    duration: 0.85,
    delay: 0.12,
    ease: 'power3.out',
    stagger: 0.12,
    clearProps: 'transform,opacity,visibility',
  });
}

export function ScrollAnimations() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    let animationContext;
    let observer;
    let refreshTimer;
    let frame;

    frame = window.requestAnimationFrame(() => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        return;
      }

      const isRtl = document.documentElement.dir === 'rtl';

      animationContext = gsap.context(() => {
        animateHero();
        document.querySelectorAll(revealSelectors.join(',')).forEach(animateReveal);
        staggerGroups.forEach(([containerSelector, itemSelector]) => {
          document.querySelectorAll(containerSelector).forEach((container) => {
            animateStaggerGroup(container, itemSelector);
          });
        });
        animateProductIntro(isRtl);
        animateConfiguration();
        animateImageParallax();
      }, document.body);

      refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 300);
      observer = new MutationObserver(() => {
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 120);
      });
      observer.observe(document.querySelector('main') || document.body, {
        childList: true,
        subtree: true,
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(refreshTimer);
      observer?.disconnect();
      animationContext?.revert();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [pathname]);

  return null;
}
